import * as React from 'react';
import cx from 'classnames';
import px from 'prop-types';
import { useTranslation } from 'Common/hooks';
import { Translation } from 'Common/components/localization';

function Container({ children, enabled, className, label, onClick }) {
    const onKeyPress = React.useMemo(() => (onClick ? (e) => (e.key === 'Enter' ? onClick() : null) : null), [onClick]);

    return enabled && onClick ? (
        <div
            className={cx('ProductRating', className)}
            role="button"
            aria-label={label}
            tabIndex="1"
            onClick={onClick}
            onKeyPress={onKeyPress}
        >
            {children}
        </div>
    ) : (
        <div className={cx('ProductRating', className)}>{children}</div>
    );
}

Container.propTypes = {
    className: px.string,
    children: px.node,
    enabled: px.bool,
    label: px.string,
    onClick: px.func,
};

export default function ProductRating({
    className,
    reviewCount = 0,
    center = true,
    slimView = false,
    reviewProviderEntityId,
    writeReviewAnchor,
    isDiscontinued,
    leftAlignBreakpoint = 'md',
    forceInline = true,
    hideLink = false,
}) {
    const reviewQtySingular = useTranslation('Commerce.Product.ReviewQuantity.Singular.Label');
    const reviewQtyPlural = useTranslation('Commerce.Product.ReviewQuantity.Plural.Label');
    const reviewsLabel = React.useMemo(
        () => `${reviewCount} ${reviewCount === 1 ? reviewQtySingular : reviewQtyPlural}`,
        [reviewCount, reviewQtySingular, reviewQtyPlural]
    );

    const goToReviews = React.useCallback(
        (write = false) => {
            if (writeReviewAnchor === 'reviews') {
                const reviewButton = document.getElementById(`${writeReviewAnchor}_button`);
                const reviewSection = document.getElementById(writeReviewAnchor);

                if (reviewSection && !reviewSection.getAttribute('aria-expanded')) {
                    reviewSection.setAttribute('aria-expanded', 'true');
                }
                if (reviewSection && !reviewSection.classList.contains('active')) {
                    reviewSection.classList.add('active', 'show');
                }
                if (reviewButton && !reviewButton.classList.contains('active')) {
                    reviewButton.classList.add('active');
                }
            } else {
                const accordionContent = document.getElementById(writeReviewAnchor);
                const accordionButton = document.getElementById(`${writeReviewAnchor}__button`);

                if (accordionButton && !accordionContent?.getAttribute('aria-expanded')) {
                    accordionButton.setAttribute('aria-expanded', 'true');
                }
                if (accordionContent && !accordionContent.classList.contains('show')) {
                    accordionContent.classList.add('show');
                }
                if (accordionButton && accordionButton.classList.contains('collapsed')) {
                    accordionButton.classList.remove('collapsed');
                }
            }

            if (write) {
                const writeReviewWrapper = document.querySelector('.write-review-wrapper');
                const connectWrapper = document.querySelector('.connect-wrapper.visible');

                if (writeReviewWrapper) {
                    writeReviewWrapper.style.display = 'block';
                }
                if (connectWrapper) {
                    connectWrapper.style.display = 'block';
                }
            }

            const targetElement = document.getElementById(writeReviewAnchor);

            if (targetElement) {
                targetElement.scrollIntoView();
            }
        },
        [writeReviewAnchor]
    );

    const onWriteReview = React.useCallback(
        (e) => {
            e.stopPropagation();
            goToReviews(true);
        },
        [goToReviews]
    );

    const onGotoReviews = React.useCallback(() => goToReviews(false), [goToReviews]);

    React.useEffect(() => {
        try {
            window.yotpo?.refreshWidgets();
        } catch (e) {
            console.error('Yotpo widget unavailable', e);
        }
    }, [reviewProviderEntityId]);

    return (
        <Container
            className={cx({ feedback: !slimView }, className)}
            onClick={onGotoReviews}
            label={reviewsLabel}
        >
            {slimView ? (
                <div className={cx(center && 'ProductRating__BottomLine align-items-center justify-content-center')}>
                    <div
                        className="yotpo bottomLine"
                        data-yotpo-product-id={reviewProviderEntityId}
                        data-yotpo-section-id={'collection'}
                    ></div>
                </div>
            ) : (
                <div className={`ProductRating__BottomLine pt-2 d-flex justify-content-center justify-content-${leftAlignBreakpoint}-start align-items-center`}>
                    <div
                        className={`ProductRating__BottomLine__Stars p-0 d-flex align-items-center`}
                    >
                        <div
                            className="yotpo bottomLine"
                            data-yotpo-product-id={reviewProviderEntityId}
                            data-yotpo-section-id={'product'}
                        ></div>
                    </div>
                    {hideLink ? null : (
                        <div
                            className={`ProductRating__BottomLine__Link ${
                                forceInline ? '' : 'mt-2'
                            } mt-lg-0 d-flex align-items-center`}
                        >
                            <div className="row">
                                <p className="paragraph-3 mb-0 strong">
                                    {isDiscontinued || !writeReviewAnchor ? null : (
                                        <span>
                                            <span className="reviewSeparator">{'|'}</span>
                                            <a className="writeReview" onClick={onWriteReview}>
                                                <Translation id="Commerce.Product.Link.WriteReview.Label" />
                                            </a>
                                        </span>
                                    )}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </Container>
    );
}

ProductRating.propTypes = {
    className: px.string,
    reviewCount: px.number,
    center: px.bool,
    hideLink: px.bool,
    isDiscontinued: px.bool,
    slimView: px.bool,
    reviewProviderEntityId: px.string,
    writeReviewAnchor: px.string,
    forceInline: px.bool,
    leftAlignBreakpoint: px.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
};
